import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";
import SmartHistoryPanel from "@/components/customer/panel/panels/SmartHistoryPanel";
import AfterSalesPanel from "@/components/customer/panel/panels/AfterSalesPanel";
import { ResizableHandle, ResizablePanel } from "@/components/ui/resizable";
import AttachmentsPanel from "./panels/AttachmentsPanel";

interface SidePanelProps {
  panelId: string;
  panelDefaultSize: number | undefined;
  panelOrder: number;
  showSmartHistory: boolean;
  showAfterSales: boolean;
  showAttachments: boolean;
  setInputMessage: (message: string) => void;
  attachments: {
    id: string;
    name: string;
    contentType: string;
    size: number;
    isInline?: boolean;
  }[];
  onDownloadAttachment: (attachmentId: string) => void;
}

export default function SidePanel({
  panelId,
  panelDefaultSize,
  panelOrder,
  showSmartHistory,
  showAfterSales,
  showAttachments,
  setInputMessage,
  attachments,
  onDownloadAttachment,
}: SidePanelProps) {
  if (!showSmartHistory && !showAfterSales && !showAttachments) {
    return null;
  }

  return (
    <>
      <ResizableHandle />
      <ResizablePanel
        id={panelId}
        defaultSize={panelDefaultSize}
        minSize={12}
        maxSize={50}
        order={panelOrder}
      >
        <div className="h-full bg-black/3 p-4 space-y-4">
          {showSmartHistory && (
            <Collapsible defaultOpen className="border-b">
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  className="w-full flex justify-between p-4"
                >
                  <span>Kundenhistorie</span>
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="my-2">
                <SmartHistoryPanel />
              </CollapsibleContent>
            </Collapsible>
          )}

          {showAfterSales && (
            <Collapsible defaultOpen className="border-b">
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  className="w-full flex justify-between p-4"
                >
                  <span>Sales Assistent</span>
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="my-2">
                <AfterSalesPanel setInputMessage={setInputMessage} />
              </CollapsibleContent>
            </Collapsible>
          )}

          {showAttachments && (() => {
            const nonInlineAttachments = attachments.filter(a => !a.isInline);
            const hasAttachments = nonInlineAttachments.length > 0;
            return (
              <Collapsible defaultOpen className={`border-b${!hasAttachments ? " opacity-50" : ""}`}>
                <CollapsibleTrigger asChild>
                  <Button
                    variant="ghost"
                    className="w-full flex justify-between p-4"
                  >
                    <span>Anhänge</span>
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="my-2">
                  <AttachmentsPanel
                    attachments={nonInlineAttachments}
                    onDownload={onDownloadAttachment}
                  />
                </CollapsibleContent>
              </Collapsible>
            );
          })()}
        </div>
      </ResizablePanel>
    </>
  );
}
